import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Create axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle response errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear auth and redirect to login
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (email, password) => {
    const response = await apiClient.post('/api/auth/login', { email, password });
    return response.data;
  },
  
  register: async (userData) => {
    const response = await apiClient.post('/api/auth/register', userData);
    return response.data;
  },
  
  getCurrentUser: async () => {
    const response = await apiClient.get('/api/auth/me');
    return response.data;
  },
};

// Company API
export const companyAPI = {
  create: async (companyData) => {
    const response = await apiClient.post('/api/companies', companyData);
    return response.data;
  },
  
  get: async (companyId) => {
    const response = await apiClient.get(`/api/companies/${companyId}`);
    return response.data;
  },
  
  update: async (companyId, updateData) => {
    const response = await apiClient.put(`/api/companies/${companyId}`, updateData);
    return response.data;
  },
};

// Vehicle API
export const vehicleAPI = {
  getYears: async () => {
    const response = await apiClient.get('/api/vehicles/years');
    return response.data;
  },
  
  getTypes: async () => {
    const response = await apiClient.get('/api/vehicles/types');
    return response.data;
  },
  
  getMakes: async (vehicleType = 'car', year = null) => {
    const params = new URLSearchParams({ vehicle_type: vehicleType });
    if (year) params.append('year', year);
    const response = await apiClient.get(`/api/vehicles/makes?${params}`);
    return response.data;
  },
  
  getModels: async (make, year, vehicleType = 'car') => {
    const params = new URLSearchParams({ make, year, vehicle_type: vehicleType });
    const response = await apiClient.get(`/api/vehicles/models?${params}`);
    return response.data;
  },
  
  getDetails: async (year, make, model) => {
    const params = new URLSearchParams({ year, make, model });
    const response = await apiClient.get(`/api/vehicles/details?${params}`);
    return response.data;
  },
  
  decodeVIN: async (vin) => {
    const params = new URLSearchParams({ vin });
    const response = await apiClient.get(`/api/vehicles/decode-vin?${params}`);
    return response.data;
  },
  
  list: async (vehicleType = null, skip = 0, limit = 100) => {
    const params = new URLSearchParams({ skip, limit });
    if (vehicleType) params.append('vehicle_type', vehicleType);
    const response = await apiClient.get(`/api/vehicles?${params}`);
    return response.data;
  },
  
  create: async (vehicleData) => {
    const response = await apiClient.post('/api/vehicles', vehicleData);
    return response.data;
  },
  
  get: async (vehicleId) => {
    const response = await apiClient.get(`/api/vehicles/${vehicleId}`);
    return response.data;
  },
  
  update: async (vehicleId, updateData) => {
    const response = await apiClient.put(`/api/vehicles/${vehicleId}`, updateData);
    return response.data;
  },
  
  delete: async (vehicleId) => {
    const response = await apiClient.delete(`/api/vehicles/${vehicleId}`);
    return response.data;
  },
};

// Inventory API
export const inventoryAPI = {
  list: async (category = null, search = null, skip = 0, limit = 100) => {
    const params = new URLSearchParams({ skip, limit });
    if (category) params.append('category', category);
    if (search) params.append('search', search);
    const response = await apiClient.get(`/api/inventory?${params}`);
    return response.data;
  },
  
  get: async (itemId) => {
    const response = await apiClient.get(`/api/inventory/${itemId}`);
    return response.data;
  },
  
  create: async (itemData) => {
    const response = await apiClient.post('/api/inventory', itemData);
    return response.data;
  },
  
  update: async (itemId, updateData) => {
    const response = await apiClient.put(`/api/inventory/${itemId}`, updateData);
    return response.data;
  },
  
  delete: async (itemId) => {
    const response = await apiClient.delete(`/api/inventory/${itemId}`);
    return response.data;
  },
};

// Customer API
export const customerAPI = {
  list: async (search = null, skip = 0, limit = 100) => {
    const params = new URLSearchParams({ skip, limit });
    if (search) params.append('search', search);
    const response = await apiClient.get(`/api/customers?${params}`);
    return response.data;
  },
  
  get: async (customerId) => {
    const response = await apiClient.get(`/api/customers/${customerId}`);
    return response.data;
  },
  
  create: async (customerData) => {
    const response = await apiClient.post('/api/customers', customerData);
    return response.data;
  },

  createWithJob: async (data) => {
    const response = await apiClient.post('/api/customers/with-job', data);
    return response.data;
  },
  
  update: async (customerId, updateData) => {
    const response = await apiClient.put(`/api/customers/${customerId}`, updateData);
    return response.data;
  },
  
  delete: async (customerId) => {
    const response = await apiClient.delete(`/api/customers/${customerId}`);
    return response.data;
  },
};

// Job API
export const jobAPI = {
  list: async (status = null, technicianId = null, customerId = null, skip = 0, limit = 100) => {
    const params = new URLSearchParams({ skip, limit });
    if (status) params.append('status', status);
    if (technicianId) params.append('technician_id', technicianId);
    if (customerId) params.append('customer_id', customerId);
    const response = await apiClient.get(`/api/jobs?${params}`);
    return response.data;
  },
  
  get: async (jobId) => {
    const response = await apiClient.get(`/api/jobs/${jobId}`);
    return response.data;
  },
  
  create: async (jobData) => {
    const response = await apiClient.post('/api/jobs', jobData);
    return response.data;
  },
  
  update: async (jobId, updateData) => {
    const response = await apiClient.put(`/api/jobs/${jobId}`, updateData);
    return response.data;
  },
  
  delete: async (jobId) => {
    const response = await apiClient.delete(`/api/jobs/${jobId}`);
    return response.data;
  },

  advance: async (jobId) => {
    const response = await apiClient.post(`/api/jobs/${jobId}/advance`);
    return response.data;
  },
};

// Quote API
export const quoteAPI = {
  list: async (status = null, customerId = null, skip = 0, limit = 100) => {
    const params = new URLSearchParams({ skip, limit });
    if (status) params.append('status', status);
    if (customerId) params.append('customer_id', customerId);
    const response = await apiClient.get(`/api/quotes?${params}`);
    return response.data;
  },
  
  get: async (quoteId) => {
    const response = await apiClient.get(`/api/quotes/${quoteId}`);
    return response.data;
  },
  
  create: async (quoteData) => {
    const response = await apiClient.post('/api/quotes', quoteData);
    return response.data;
  },
  
  update: async (quoteId, updateData) => {
    const response = await apiClient.put(`/api/quotes/${quoteId}`, updateData);
    return response.data;
  },
};

// Purchase API
export const purchaseAPI = {
  list: async (skip = 0, limit = 100) => {
    const params = new URLSearchParams({ skip, limit });
    const response = await apiClient.get(`/api/purchases?${params}`);
    return response.data;
  },
  
  create: async (purchaseData) => {
    const response = await apiClient.post('/api/purchases', purchaseData);
    return response.data;
  },
};

// Equipment API
export const equipmentAPI = {
  list: async (category = null, skip = 0, limit = 100) => {
    const params = new URLSearchParams({ skip, limit });
    if (category) params.append('category', category);
    const response = await apiClient.get(`/api/equipment?${params}`);
    return response.data;
  },
  
  create: async (equipmentData) => {
    const response = await apiClient.post('/api/equipment', equipmentData);
    return response.data;
  },
};

// Gas Expense API
export const gasExpenseAPI = {
  list: async (technicianId = null, skip = 0, limit = 100) => {
    const params = new URLSearchParams({ skip, limit });
    if (technicianId) params.append('technician_id', technicianId);
    const response = await apiClient.get(`/api/gas-expenses?${params}`);
    return response.data;
  },
  
  create: async (expenseData) => {
    const response = await apiClient.post('/api/gas-expenses', expenseData);
    return response.data;
  },
};

// Reports API
export const reportsAPI = {
  getDashboard: async (days = 30) => {
    const params = new URLSearchParams({ days });
    const response = await apiClient.get(`/api/reports/dashboard?${params}`);
    return response.data;
  },
  
  getRevenue: async (startDate = null, endDate = null) => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    const response = await apiClient.get(`/api/reports/revenue?${params}`);
    return response.data;
  },
  
  getTechnician: async (technicianId, days = 30) => {
    const params = new URLSearchParams({ days });
    const response = await apiClient.get(`/api/reports/technician/${technicianId}?${params}`);
    return response.data;
  },
};

// User API
export const userAPI = {
  list: async () => {
    const response = await apiClient.get('/api/users');
    return response.data;
  },
  
  update: async (userId, updateData) => {
    const response = await apiClient.put(`/api/users/${userId}`, updateData);
    return response.data;
  },
};

// Phone Calls API
export const phoneCallAPI = {
  list: async (status = null, technicianId = null) => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (technicianId) params.append('technician_id', technicianId);
    const response = await apiClient.get(`/api/calls?${params}`);
    return response.data;
  },
  create: async (data) => {
    const response = await apiClient.post('/api/calls', data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await apiClient.put(`/api/calls/${id}`, data);
    return response.data;
  },
};

// SMS API
export const smsAPI = {
  list: async (customerId = null) => {
    const params = new URLSearchParams();
    if (customerId) params.append('customer_id', customerId);
    const response = await apiClient.get(`/api/sms?${params}`);
    return response.data;
  },
  send: async (data) => {
    const response = await apiClient.post('/api/sms', data);
    return response.data;
  },
};

// Phone Proxies API
export const phoneProxyAPI = {
  list: async () => {
    const response = await apiClient.get('/api/phone-proxies');
    return response.data;
  },
  create: async (data) => {
    const response = await apiClient.post('/api/phone-proxies', data);
    return response.data;
  },
  delete: async (id) => {
    const response = await apiClient.delete(`/api/phone-proxies/${id}`);
    return response.data;
  },
};

// Reminders API
export const reminderAPI = {
  list: async (upcomingOnly = false, type = null) => {
    const params = new URLSearchParams();
    if (upcomingOnly) params.append('upcoming_only', 'true');
    if (type) params.append('type', type);
    const response = await apiClient.get(`/api/reminders?${params}`);
    return response.data;
  },
  create: async (data) => {
    const response = await apiClient.post('/api/reminders', data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await apiClient.put(`/api/reminders/${id}`, data);
    return response.data;
  },
  delete: async (id) => {
    const response = await apiClient.delete(`/api/reminders/${id}`);
    return response.data;
  },
};

// Pricing Calculator API
export const pricingAPI = {
  getSettings: async () => {
    const response = await apiClient.get('/api/pricing-settings');
    return response.data;
  },
  updateSettings: async (data) => {
    const response = await apiClient.put('/api/pricing-settings', data);
    return response.data;
  },
  calculate: async (data) => {
    const response = await apiClient.post('/api/pricing-calculate', data);
    return response.data;
  },
};

// AI Assistant API
export const aiAssistantAPI = {
  chat: async (message, sessionId = null) => {
    const response = await apiClient.post('/api/ai-assistant/chat', { message, session_id: sessionId });
    return response.data;
  },
  listSessions: async () => {
    const response = await apiClient.get('/api/ai-assistant/sessions');
    return response.data;
  },
  getSession: async (sessionId) => {
    const response = await apiClient.get(`/api/ai-assistant/sessions/${sessionId}`);
    return response.data;
  },
};

// Documents API
export const documentAPI = {
  list: async (customerId = null, jobId = null, category = null) => {
    const params = new URLSearchParams();
    if (customerId) params.append('customer_id', customerId);
    if (jobId) params.append('job_id', jobId);
    if (category) params.append('category', category);
    const response = await apiClient.get(`/api/documents?${params}`);
    return response.data;
  },
  create: async (data) => {
    const response = await apiClient.post('/api/documents', data);
    return response.data;
  },
  delete: async (id) => {
    const response = await apiClient.delete(`/api/documents/${id}`);
    return response.data;
  },
};

// Dispatch API
export const dispatchAPI = {
  getOverview: async () => {
    const response = await apiClient.get('/api/dispatch/overview');
    return response.data;
  },
};

// Billing API
export const billingAPI = {
  getPlans: async () => {
    const response = await apiClient.get('/api/billing/plans');
    return response.data;
  },
  getSubscription: async () => {
    const response = await apiClient.get('/api/billing/subscription');
    return response.data;
  },
  getMode: async () => {
    const response = await apiClient.get('/api/billing/mode');
    return response.data;
  },
  subscribeDemo: async (packageId) => {
    const response = await apiClient.post('/api/billing/subscribe', {
      package_id: packageId,
    });
    return response.data;
  },
  cancel: async () => {
    const response = await apiClient.post('/api/billing/cancel');
    return response.data;
  },
  createCheckout: async (packageId, originUrl) => {
    const response = await apiClient.post('/api/billing/checkout', {
      package_id: packageId,
      origin_url: originUrl,
    });
    return response.data;
  },
  getCheckoutStatus: async (sessionId) => {
    const response = await apiClient.get(`/api/billing/status/${sessionId}`);
    return response.data;
  },
  listTransactions: async () => {
    const response = await apiClient.get('/api/billing/transactions');
    return response.data;
  },
};

// Telephony API (Twilio call masking — demo-mode safe)
export const telephonyAPI = {
  status: async () => {
    const response = await apiClient.get('/api/telephony/status');
    return response.data;
  },
  maskCall: async ({ customer_phone, tech_phone, customer_id, technician_id, job_id, note }) => {
    const response = await apiClient.post('/api/telephony/mask-call', {
      customer_phone,
      tech_phone,
      customer_id,
      technician_id,
      job_id,
      note,
    });
    return response.data;
  },
  endCall: async (sessionId, { duration_seconds = 0, outcome = 'completed' } = {}) => {
    const response = await apiClient.post(`/api/telephony/mask-call/${sessionId}/end`, {
      duration_seconds,
      outcome,
    });
    return response.data;
  },
  listSessions: async (limit = 20) => {
    const response = await apiClient.get(`/api/telephony/sessions?limit=${limit}`);
    return response.data;
  },
};

// Invoice API
export const invoiceAPI = {
  list: async (status = null, customerId = null) => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (customerId) params.append('customer_id', customerId);
    const response = await apiClient.get(`/api/invoices?${params}`);
    return response.data;
  },
  get: async (id) => {
    const response = await apiClient.get(`/api/invoices/${id}`);
    return response.data;
  },
  create: async (data) => {
    const response = await apiClient.post('/api/invoices', data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await apiClient.put(`/api/invoices/${id}`, data);
    return response.data;
  },
  delete: async (id) => {
    const response = await apiClient.delete(`/api/invoices/${id}`);
    return response.data;
  },
  pdfUrl: (id) => `${API_BASE_URL}/api/invoices/${id}/pdf`,
  downloadPdf: async (id) => {
    const response = await apiClient.get(`/api/invoices/${id}/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  },
  email: async (id, body = {}) => {
    const response = await apiClient.post(`/api/invoices/${id}/email`, body);
    return response.data;
  },
  payLink: async (id) => {
    const response = await apiClient.post(`/api/invoices/${id}/pay-link`);
    return response.data;
  },
  convertFromQuote: async (quoteId) => {
    const response = await apiClient.post(`/api/quotes/${quoteId}/convert-to-invoice`);
    return response.data;
  },
};

// Quote PDF (extra to quoteAPI)
export const quotePdfAPI = {
  downloadPdf: async (id) => {
    const response = await apiClient.get(`/api/quotes/${id}/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  },
  email: async (id, body = {}) => {
    const response = await apiClient.post(`/api/quotes/${id}/email`, body);
    return response.data;
  },
};

// Customer-Vehicle relationship
export const customerVehicleAPI = {
  list: async (customerId) => {
    const response = await apiClient.get(`/api/customers/${customerId}/vehicles`);
    return response.data;
  },
  attach: async (customerId, vehicleId) => {
    const response = await apiClient.post(`/api/customers/${customerId}/vehicles/${vehicleId}`);
    return response.data;
  },
  detach: async (customerId, vehicleId) => {
    const response = await apiClient.delete(`/api/customers/${customerId}/vehicles/${vehicleId}`);
    return response.data;
  },
  getPortalLink: async (customerId) => {
    const response = await apiClient.get(`/api/customers/${customerId}/portal-link`);
    return response.data;
  },
};

// Job time tracking
export const jobTimerAPI = {
  start: async (jobId) => {
    const response = await apiClient.post(`/api/jobs/${jobId}/timer/start`);
    return response.data;
  },
  stop: async (jobId) => {
    const response = await apiClient.post(`/api/jobs/${jobId}/timer/stop`);
    return response.data;
  },
};

export default apiClient;
